import {ComponentType, ReactNode} from 'react';

import {createView, css, view} from '@sail/ui';

import {useTheme, Wave} from '@stripe-internal/conversations-ui';

import {ComponentWithOverride} from './ComponentWithOverride';

const paneStyles = [
  css({
    position: 'relative',
    flexGrow: '1',
    stack: 'y',
    overflow: 'hidden',
  }),
];

function Pane({children}: {children: ReactNode}) {
  return (
    <view.div data-testid="conversations-widget-pane" uses={paneStyles}>
      {children}
    </view.div>
  );
}

type Props = {
  header?: ReactNode | ComponentType;
  footer?: ReactNode | ComponentType;
  errorBoundary?: ComponentType<{children: ReactNode}> | null;
  showWave?: boolean;
  children?: ReactNode;
};

export const AssistantContainer = createView(
  ({
    header,
    footer,
    errorBoundary,
    showWave = true,
    children,
    ...props
  }: Props) => {
    const theme = useTheme();
    const content = (
      <>
        {showWave ? (
          <Wave
            css={{
              position: 'absolute',
              width: 'fill',
              top: '0px',
            }}
            focused={false}
            loading={false}
            audience={theme.audience}
          />
        ) : null}
        <Pane>{children}</Pane>
        <ComponentWithOverride override={footer} component={null} />
      </>
    );

    const ErrorBoundaryComponent = errorBoundary;
    return (
      <view.div
        data-testid="conversations-widget-root"
        css={{
          overflow: 'hidden',
          position: 'relative',
          stack: 'y',
          height: 'fill',
          backgroundColor: 'white',
        }}
        {...props}
      >
        <ComponentWithOverride override={header} component={null} />
        <view.div
          css={{
            height: 'fill',
            overflow: 'clip',
            position: 'relative',
            stack: 'y',
            ':before': {
              content: '""',
              position: 'absolute',
              top: '-80px',
              left: 'space.0',
              right: 'space.0',
              height: '430px',
              width: 'fill',
              backgroundSize: 'cover',
            },
          }}
        >
          {ErrorBoundaryComponent ? (
            <ErrorBoundaryComponent>{content}</ErrorBoundaryComponent>
          ) : (
            content
          )}
        </view.div>
      </view.div>
    );
  },
);
